import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faPaperclip, 
  faTimes,
  faPencilAlt,
  faTasks,
  faCalendarAlt,
  faSave,
  faChevronRight,
  faInfoCircle,
  faCalendarCheck,
  faBuilding,
  faHotel,
  faRoute,
  faCalendar,
  faCheckCircle,
  faBan,
  faTrash,
  faMapMarkerAlt,
  faSuitcase,
  faClipboardCheck,
  faAddressBook,
  faUser,
  faPhone,
  faCar,
  faEdit,
  faUserTie,
  faComments,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import TourContext from '../../context/TourContext';
import UserContext from '../../context/UserContext';
import AddScheduleModal from './AddScheduleModal';
import ScheduleCard from './ScheduleCard';
import ContactModal from './ContactModal';
import TopBar from '../TopBar';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import { 
  query, 
  collection, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  getDoc, 
  Timestamp, 
  deleteDoc, 
  increment, 
  serverTimestamp, 
  setDoc, 
  addDoc, 
  orderBy,
  limit,
  writeBatch
} from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { sendEventDateRescheduledNotification, sendEventDateStatusNotification } from '../../utils/NotificationService';
import Event from './Event';
import Hotel from './Hotel';
import Route from './Route';
import styles from './Schedule.module.css';
import AssignedInventoryTab from './AssignedInventoryTab';
import { decrementUserStats } from '../../utils/actions/userActions';
import defaultUserProfilePicture from '../../assets/Images/defaultUserProfilePicture.png'

const Schedule = () => {
  const { 
    selectedEvent, 
    selectedDate, 
    updateDate,  
    getSelectedSectionData, 
    selectDate,
    getSelectedDate,
    setSelectedEvent,
    setSelectedDate,
  } = useContext(TourContext);
  const { userData, setUserData, authToken } = useContext(UserContext);
  const navigate = useNavigate();
  const { eventId, dateId } = useParams();
  const location = useLocation();

  // State declarations
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [notes, setNotes] = useState([]);
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('All');
  const [schedules, setSchedules] = useState([]);
  const [attachmentCount, setAttachmentCount] = useState(0);
  const [taskCount, setTaskCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [events, setEvents] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    // Default to 'event' for Vendor/Staff users
    if (userData?.userType === 'Vendor' || userData?.userType === 'Staff') {
      return 'event';
    }
    
    // Use the activeTab from location state if provided
    if (location.state?.activeTab) {
      return location.state.activeTab;
    }
    
    // Otherwise use the default 'schedule'
    return 'schedule';
  });
  const [isShowVerified, setIsShowVerified] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [isRescheduled, setIsRescheduled] = useState(false);
  const [rescheduledDate, setRescheduledDate] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [newRescheduleDate, setNewRescheduleDate] = useState(new Date());
  const [isProcessing, setIsProcessing] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [vendorIdForInventory, setVendorIdForInventory] = useState(null);

  // Contact-related states
  const [isContactModalVisible, setIsContactModalVisible] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [editingContact, setEditingContact] = useState(null);

  const [deleteModalProps, setDeleteModalProps] = useState({
    isCreator: false,
    isVerified: false,
    eventName: ''
  });

  // Determine event type from URL
  const eventType = location.pathname.includes('/tour/') ? 'tour' : 'show';

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  useEffect(() => {
    const eventDate = getSelectedDate();
    if (eventDate) {
      setIsCancelled(eventDate.cancelled || false);
      setIsRescheduled(eventDate.rescheduleDate?.isRescheduled || false);
      setRescheduledDate(eventDate.rescheduleDate?.newDate || null);
    }
  }, [selectedDate, getSelectedDate]);

  // Load schedules, notes, and counts
  useEffect(() => {
    const loadScheduleData = async () => {
      if (!selectedEvent?.id || !selectedDate) return;
      
      setIsLoading(true);
      try {
        const db = getFirestoreInstance();
        const collectionName = eventType === 'tour' ? 'tours' : 'shows';
        
        // Fetch schedules from schedule subcollection
        const schedulesQuery = query(
          collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'schedule'),
          orderBy('createdAt', 'desc')
        );
        
        const schedulesSnapshot = await getDocs(schedulesQuery);
        const schedulesList = schedulesSnapshot.docs.map(doc => ({
          docId: doc.id,  // Store the Firestore document ID as 'docId' to avoid collision
          ...doc.data()   // This spreads doc data which may include its own 'id' field
        }));
        
        setSchedules(schedulesList);
        
        // Fetch notes from notes subcollection
        const notesQuery = query(
          collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'notes'),
          orderBy('createdAt', 'desc')
        );
        
        const notesSnapshot = await getDocs(notesQuery);
        const notesList = notesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setNotes(notesList);
        
        // Fetch contacts from contacts subcollection
        const contactsQuery = query(
          collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'contacts')
        );
        
        const contactsSnapshot = await getDocs(contactsQuery);
        const contactsList = contactsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setContacts(contactsList);
        
        // Get counts of attachments and tasks
        const attachmentsQuery = query(
          collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'attachments')
        );
        const attachmentsSnapshot = await getDocs(attachmentsQuery);
        setAttachmentCount(attachmentsSnapshot.size);
        
        const tasksQuery = query(
          collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'tasks')
        );
        const tasksSnapshot = await getDocs(tasksQuery);
        setTaskCount(tasksSnapshot.size);
      } catch (error) {
        console.error('Error loading schedule data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadScheduleData();
  }, [selectedEvent, selectedDate, eventType]);

  useEffect(() => {
    console.log('Schedule received params:', { eventId, dateId });
    console.log('Schedule current events:', events);
    console.log('Schedule selectedEvent:', selectedEvent);
    
    if (eventId && dateId) {
      if (!selectedEvent || selectedEvent.id !== eventId) {
        const event = events.find(e => e.id === eventId);
        console.log('Found event in Schedule:', event);
        if (event) {
          setSelectedEvent(event);
        }
      }
      if (dateId) {
        setSelectedDate(dateId);
      }
    }
  }, [eventId, dateId, events, selectedEvent, setSelectedEvent, setSelectedDate]);

  useEffect(() => {
    if (selectedEvent) {
      // If we have an event but no date, select the first date
      if (!selectedDate && selectedEvent.eventDates?.length > 0) {
        const firstDate = selectedEvent.eventDates[0];
        setSelectedDate(firstDate.id);
        navigate(`/${eventType}/${selectedEvent.id}/${firstDate.id}`);
        return;
      }
  
      // If we have both event and date
      if (selectedDate) {
        const currentEventDate = selectedEvent.eventDates.find(date => date.id === selectedDate);
        if (!currentEventDate) {
          // If the selected date no longer exists in the event, navigate back
          navigate(`/${userData.userName}`);
          return;
        }
  
        const newPath = `/${eventType}/${selectedEvent.id}/${selectedDate}`;
        if (location.pathname !== newPath) {
          navigate(newPath);
        }
      }
    }
  }, [selectedEvent, selectedDate, navigate, location, userData.userName, eventType]);

  // Reset verification status when event or date changes
  useEffect(() => {
    setVerificationStatus(null);
    checkVerificationStatus();
  }, [selectedEvent, selectedDate, userData]);

  // Clear error messages after a timeout
  useEffect(() => {
    if (verificationStatus && !verificationStatus.includes('successfully')) {
      const timer = setTimeout(() => {
        setVerificationStatus(null);
      }, 8000);
      
      return () => clearTimeout(timer);
    }
  }, [verificationStatus]);

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
    
    if (location.state?.vendorId) {
      setVendorIdForInventory(location.state.vendorId);
    }
  }, [location]);
  
  useEffect(() => {
    if (location.state?.editScheduleId) {
      const scheduleToEdit = schedules.find(s => s.id === location.state.editScheduleId);
      if (scheduleToEdit) {
        setEditingSchedule(scheduleToEdit);
        setIsModalVisible(true);
      }
    }
  }, [location.state?.editScheduleId, schedules]);

  const clearVerificationStatus = () => {
    setVerificationStatus(null);
  };

  // Notes handling functions
  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim() || !selectedEvent?.id || !selectedDate) return;

    setIsSavingNote(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';

      // Add new note to the notes subcollection
      const noteRef = collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'notes');
      
      await addDoc(noteRef, {
        content: newNote,
        createdBy: {
          userId: userData.userId,
          fullName: userData.fullName,
          userName: userData.userName,
          profilePictureUrl: userData.profilePictureUrl || ''
        },
        createdAt: serverTimestamp()
      });

      // Clear input
      setNewNote('');
      
      // Refresh notes list
      const notesQuery = query(
        collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'notes'),
        orderBy('createdAt', 'desc')
      );
      
      const notesSnapshot = await getDocs(notesQuery);
      const notesList = notesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setNotes(notesList);
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Failed to add note. Please try again.');
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteContent(note.content);
  };

  const handleSaveEditedNote = async () => {
    if (!editingNoteContent.trim() || !selectedEvent?.id || !selectedDate) return;

    setIsSavingNote(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Update the note in the notes subcollection
      const noteRef = doc(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'notes', editingNoteId);
      
      await updateDoc(noteRef, {
        content: editingNoteContent,
        updatedAt: serverTimestamp()
      });

      // Reset editing state
      setEditingNoteId(null);
      setEditingNoteContent('');
      
      // Refresh notes list
      const notesQuery = query(
        collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'notes'),
        orderBy('createdAt', 'desc')
      );
      
      const notesSnapshot = await getDocs(notesQuery);
      const notesList = notesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setNotes(notesList);
    } catch (error) {
      console.error('Error updating note:', error);
      alert('Failed to update note. Please try again.');
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    if (!selectedEvent?.id || !selectedDate) return;

    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Delete the note from the notes subcollection
      const noteRef = doc(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'notes', noteId);
      await deleteDoc(noteRef);
      
      // Update notes state
      setNotes(prevNotes => prevNotes.filter(note => note.id !== noteId));
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note. Please try again.');
    }
  };

  // Contact handling functions
  const handleAddContact = async (contactData, isEditing) => {
    if (!selectedEvent?.id || !selectedDate) return;

    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      if (isEditing) {
        // Update existing contact
        const contactRef = doc(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'contacts', contactData.id);
        await updateDoc(contactRef, contactData);
      } else {
        // Add new contact
        const contactRef = collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'contacts');
        await addDoc(contactRef, {
          ...contactData,
          createdAt: serverTimestamp()
        });
      }

      // Refresh contacts list
      const contactsQuery = query(
        collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'contacts')
      );
      
      const contactsSnapshot = await getDocs(contactsQuery);
      const contactsList = contactsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setContacts(contactsList);
      setIsContactModalVisible(false);
      setEditingContact(null);
    } catch (error) {
      console.error('Error adding/updating contact:', error);
      alert('Failed to save contact. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setIsContactModalVisible(true);
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    if (!selectedEvent?.id || !selectedDate) return;

    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      const contactRef = doc(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'contacts', contactId);
      
      await deleteDoc(contactRef);
      
      // Update contacts state
      setContacts(prevContacts => prevContacts.filter(contact => contact.id !== contactId));
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Failed to delete contact. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const checkVerificationStatus = () => {
    if (!selectedEvent?.id || !selectedDate || !userData?.userId) return;
    
    // Get details about which array the user belongs to
    let assignedDates = [];
    
    if (userData.userType === 'Artist' && Array.isArray(selectedEvent.artistDetails)) {
      const artist = selectedEvent.artistDetails.find(a => a.userId === userData.userId);
      if (artist && artist.assignedDates) {
        assignedDates = artist.assignedDates;
      }
    } else if (userData.userType === 'Crew' && Array.isArray(selectedEvent.crewDetails)) {
      const crew = selectedEvent.crewDetails.find(c => c.userId === userData.userId);
      if (crew && crew.assignedDates) {
        assignedDates = crew.assignedDates;
      }
    } else if (userData.userType === 'Manager') {
      // Handle managerDetails which could be an array or object
      if (Array.isArray(selectedEvent.managerDetails)) {
        const manager = selectedEvent.managerDetails.find(m => m.userId === userData.userId);
        if (manager && manager.assignedDates) {
          assignedDates = manager.assignedDates;
        }
      } else if (selectedEvent.managerDetails && typeof selectedEvent.managerDetails === 'object') {
        // Handle object/map structure
        Object.values(selectedEvent.managerDetails).forEach(manager => {
          if (manager.userId === userData.userId && manager.assignedDates) {
            assignedDates = manager.assignedDates;
          }
        });
      }
    } else if (userData.userType === 'Vendor' && Array.isArray(selectedEvent.vendorDetails)) {
      // Add this section to check verification status for vendors
      const vendor = selectedEvent.vendorDetails.find(v => v.userId === userData.userId);
      if (vendor && vendor.assignedDates) {
        assignedDates = vendor.assignedDates;
      }
    } else if (userData.userType === 'Staff' && Array.isArray(selectedEvent.staffDetails)) {
      // Add this section to check verification status for staff
      const staff = selectedEvent.staffDetails.find(s => s.userId === userData.userId);
      if (staff && staff.assignedDates) {
        assignedDates = staff.assignedDates;
      }
    }
    
    // Check if this date is verified
    let isVerified = false;
    
    // Handle both array and object structures
    if (Array.isArray(assignedDates)) {
      const dateAssignment = assignedDates.find(date => date.tourDateDocId === selectedDate);
      if (dateAssignment && dateAssignment.verified) {
        isVerified = true;
      }
    } else if (typeof assignedDates === 'object') {
      // It's an object/map
      Object.values(assignedDates).forEach(date => {
        if (date.tourDateDocId === selectedDate && date.verified) {
          isVerified = true;
        }
      });
    }
    
    setIsShowVerified(isVerified);
  };

  // Add this function to verify attendance
  const handleVerifyAttendance = async () => {
    if (!selectedEvent?.id || !selectedDate || !userData?.userId) return;
    
    setIsVerifying(true);
    setVerificationStatus(null);
    
    try {
      // Get the tour date and venue information
      const eventDate = getSelectedDate();
      const venueData = eventDate?.event?.venueDetails?.venueData;
      
      if (!venueData || !venueData.location) {
        setVerificationStatus('No venue location data available');
        setIsVerifying(false);
        return;
      }
      
      // Get user's current location
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });
      
      const userLat = position.coords.latitude;
      const userLng = position.coords.longitude;
      const venueLat = venueData.location.latitude;
      const venueLng = venueData.location.longitude;
      
      // Calculate distance (using Haversine formula)
      const distance = calculateDistance(userLat, userLng, venueLat, venueLng);
      
      // If within 500 meters, consider it verified
      if (distance <= 0.5) {
        // Update the verification status in database
        const db = getFirestoreInstance();
        const collectionName = eventType === 'tour' ? 'tours' : 'shows';
        
        // Check if the assigned user exists in the assignedUsers subcollection
        const assignedUserRef = doc(db, collectionName, selectedEvent.id, 'assignedUsers', userData.userId);
        const assignedUserDoc = await getDoc(assignedUserRef);
        
        if (assignedUserDoc.exists()) {
          // Update the assigned user document
          const assignedUserData = assignedUserDoc.data();
          const assignedDates = assignedUserData.assignedDates || [];
          
          // Find the date in the assigned dates
          const dateIndex = assignedDates.findIndex(date => date.dateDocId === selectedDate);
          
          if (dateIndex !== -1) {
            // Update the date to verified
            assignedDates[dateIndex] = {
              ...assignedDates[dateIndex],
              verified: true
            };
          } else {
            // Add the date if it doesn't exist
            assignedDates.push({
              dateDocId: selectedDate,
              date: eventDate.date,
              verified: true
            });
          }
          
          // Update the assignedDates field
          await updateDoc(assignedUserRef, {
            assignedDates
          });
          
          // Increment the verified count
          await updateDoc(doc(db, 'users', userData.userId), {
            verifiedShowsCount: increment(1)
          });
          
          // Update user_events collection
          try {
            // Create or update the user event document
            const userEventRef = doc(db, 'user_events', userData.userId, 'events', selectedEvent.id);
            await setDoc(userEventRef, {
              eventId: selectedEvent.id,
              eventType,
              lastUpdated: serverTimestamp()
            }, { merge: true });
            
            // Update the event date document
            const userEventDateRef = doc(db, 'user_events', userData.userId, 'events', selectedEvent.id, 'eventDates', selectedDate);
            await setDoc(userEventDateRef, {
              verified: true,
              verifiedAt: serverTimestamp(),
              lastUpdated: serverTimestamp()
            }, { merge: true });
          } catch (error) {
            console.error('Error updating user_events collection:', error);
          }
          
          setVerificationStatus('Attendance verified successfully!');
          setIsShowVerified(true);
          
          // Refresh event data
          await updateDate(selectedEvent.id, selectedDate, eventType);
        } else {
          setVerificationStatus('You are not found in the assigned users list');
        }
      } else {
        // Set the error message
        setVerificationStatus(`You are ${distance.toFixed(2)} km away from the venue. Must be within 0.5 km to verify.`);
      }
    } catch (error) {
      console.error('Error verifying attendance:', error);
      setVerificationStatus(`Error: ${error.message}`);
    } finally {
      setIsVerifying(false);
    }
  };

  // Helper function to calculate distance
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the Earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in km
  };

  const CancelConfirmationModal = ({ isOpen, onClose, onConfirm, eventName, isProcessing, isCancelled }) => {
    if (!isOpen) return null;
  
    return (
      <div className={styles['modal-overlay']}>
        <div className={styles['modal-content']}>
          <h2 className={styles['modal-title']}>{isCancelled ? 'Un-cancel Event Date' : 'Cancel Event Date'}</h2>
          <p className={styles['modal-message']}>
            Are you sure you want to {isCancelled ? 'un-cancel' : 'cancel'} this date from {eventName}?
          </p>
          <div className={styles['modal-buttons']}>
            <button 
              className={`${styles['modal-button']} ${styles['cancel-button']}`}
              onClick={onClose}
              disabled={isProcessing}
            >
              Close
            </button>
            <button 
              className={`${styles['modal-button']} ${styles['delete-button']}`}
              onClick={onConfirm}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : (isCancelled ? 'Un-cancel' : 'Cancel')}
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Add this state near your other state declarations
  const [showCancelModal, setShowCancelModal] = useState(false);
  
  // Replace the handleCancelTourDate function with this:
  const handleCancelEventDate = () => {
    setShowCancelModal(true);
  };
  
  // Add this new function to handle the confirmation
  const confirmCancel = async () => {
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      const eventRef = doc(db, collectionName, selectedEvent.id);
      const eventDateRef = doc(eventRef, 'eventDates', selectedDate);
      const timestamp = Timestamp.now();
  
      const eventDate = getSelectedDate();
  
      await updateDoc(eventDateRef, {
        cancelled: !isCancelled,
        dateLastUpdated: timestamp
      });
  
      // Send notification
      await sendEventDateStatusNotification(
        selectedEvent,
        eventDate,
        !isCancelled, // new status
        {
          userId: userData.userId,
          userName: userData.userName,
          fullName: userData.fullName,
          profilePictureUrl: userData.profilePictureUrl
        }
      );
  
      // Update user's showsCount - this will be handled by the listener now
      setIsCancelled(!isCancelled);
      
      // Let the listener update the event data
      await updateDate(selectedEvent.id, selectedDate, eventType);
    } catch (error) {
      console.error('Error cancelling event date:', error);
      alert('Failed to update event date. Please try again.');
    } finally {
      setIsProcessing(false);
      setShowCancelModal(false);
    }
  };
  
  const handleRescheduleEventDate = () => {
    setShowRescheduleModal(true);
  };

  const handleSaveReschedule = async () => {
    if (!selectedEvent?.id || !selectedDate) return;

    // Validate the new date
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const newDate = new Date(newRescheduleDate);
    newDate.setHours(0, 0, 0, 0);

    if (newDate < currentDate) {
      alert('Cannot reschedule to a past date.');
      return;
    }

    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      const eventRef = doc(db, collectionName, selectedEvent.id);
      const eventDateRef = doc(eventRef, 'eventDates', selectedDate);
      const timestamp = Timestamp.now();

      const eventDate = getSelectedDate();
      const oldDateFormatted = new Date(eventDate.date).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
      const newDateFormatted = newDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });

      // Update the event date
      await updateDoc(eventDateRef, {
        rescheduleDate: {
          isRescheduled: true,
          newDate: newRescheduleDate.toISOString().split('T')[0]
        },
        dateLastUpdated: timestamp
      });

      // Send notification
      await sendEventDateRescheduledNotification(
        selectedEvent,
        eventDate,
        oldDateFormatted,
        newDateFormatted,
        {
          userId: userData.userId,
          userName: userData.userName,
          fullName: userData.fullName,
          profilePictureUrl: userData.profilePictureUrl
        }
      );

      setIsRescheduled(true);
      setRescheduledDate(newRescheduleDate.toISOString().split('T')[0]);
      setShowRescheduleModal(false);
      
      // Let the listener update the event date
      await updateDate(selectedEvent.id, selectedDate, eventType);
    } catch (error) {
      console.error('Error rescheduling event date:', error);
      alert('Failed to reschedule event date. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const RescheduleModal = () => (
    <div className={styles.modalOverlay} onClick={() => setShowRescheduleModal(false)}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <h2 className={styles.modalTitle}>
          {isRescheduled ? 'Update Rescheduled Date' : 'Reschedule Event Date'}
        </h2>
        <input
          type="date"
          className={styles.dateInput}
          value={newRescheduleDate.toISOString().split('T')[0]}
          onChange={(e) => setNewRescheduleDate(new Date(e.target.value))}
          min={new Date().toISOString().split('T')[0]}
        />
        <div className={styles.modalButtons}>
          {isRescheduled && (
            <button 
              className={`${styles.modalButton} ${styles.cancelButton}`}
              onClick={handleCancelReschedule}
            >
              Remove Rescheduled Date
            </button>
          )}
          <button 
            className={`${styles.modalButton} ${styles.saveButton}`}
            onClick={handleSaveReschedule}
          >
            Save
          </button>
          <button 
            className={`${styles.modalButton} ${styles.closeButton}`}
            onClick={() => setShowRescheduleModal(false)}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  const handleCancelReschedule = async () => {
    if (!selectedEvent?.id || !selectedDate) return;
    if (!window.confirm('Are you sure you want to remove the rescheduled date?')) return;

    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      const eventRef = doc(db, collectionName, selectedEvent.id);
      const eventDateRef = doc(eventRef, 'eventDates', selectedDate);
      const timestamp = Timestamp.now();

      await updateDoc(eventDateRef, {
        rescheduleDate: {
          isRescheduled: false,
          newDate: null
        },
        dateLastUpdated: timestamp
      });

      setIsRescheduled(false);
      setRescheduledDate(null);
      setShowRescheduleModal(false);
      
      // Let the listener update the event date
      await updateDate(selectedEvent.id, selectedDate, eventType);
    } catch (error) {
      console.error('Error removing rescheduled date:', error);
      alert('Failed to remove rescheduled date. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, eventName, isProcessing, isCreator, isVerified }) => {
    if (!isOpen) return null;
  
    // Generate message based on user role and verification status
    let message = '';
    
    if (isCreator) {
      message = `As the creator of this event, deleting this date will remove it for ALL members of "${eventName}". ${
        isVerified ? 
          'Note: Verified show counts will be updated accordingly.' : 
          'Note: Show counts will be decremented for all members.'
      }`;
    } else {
      message = `This will remove this date from your schedule in "${eventName}". Other members will still see this date. ${
        isVerified ? 
          'Since you have verified this show, your verified show count will be updated.' : 
          'Your show count will be decremented.'
      }`;
    }
  
    return (
      <div className={styles['modal-overlay']}>
        <div className={styles['modal-content']}>
          <h2 className={styles['modal-title']}>Delete Event Date</h2>
          <p className={styles['modal-message']}>{message}</p>
          <div className={styles['modal-buttons']}>
            <button 
              className={`${styles['modal-button']} ${styles['cancel-button']}`}
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button 
              className={`${styles['modal-button']} ${styles['delete-button']}`}
              onClick={onConfirm}
              disabled={isProcessing}
            >
              {isProcessing ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Update the delete functionality
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDeleteEventDate = async () => {
    if (!selectedEvent?.id || !selectedDate) return;
    
    // Determine if the current user is the creator
    const isCreator = selectedEvent.createdBy?.userId === userData.userId;
    
    // Check if this date is verified for the current user
    let isVerified = false;
    
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Check if the user exists in the assignedUsers subcollection
      const assignedUserRef = doc(db, collectionName, selectedEvent.id, 'assignedUsers', userData.userId);
      const assignedUserDoc = await getDoc(assignedUserRef);
      
      if (assignedUserDoc.exists()) {
        // Check if this date is verified
        const assignedUserData = assignedUserDoc.data();
        const assignedDates = assignedUserData.assignedDates || [];
        
        const dateAssignment = assignedDates.find(date => date.dateDocId === selectedDate);
        isVerified = dateAssignment?.verified === true;
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
    }
    
    // Show appropriate modal with isCreator flag and verification status
    setShowDeleteModal(true);
    setDeleteModalProps({
      isCreator,
      isVerified,
      eventName: selectedEvent.name || (eventType === 'tour' ? selectedEvent.tourName : selectedEvent.showName)
    });
  };

  const deleteFile = async (filePath, authToken) => {
    try {
      console.log('Deleting file from Backblaze B2:', filePath);
      
      // Use the deleteBackblazeFile endpoint defined in index.js
      const response = await fetch('/api/deleteBackblazeFile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ filePath })
      });
      
      // Check the response
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete file');
        } else {
          const errorText = await response.text();
          console.error('Non-JSON error response:', errorText);
          throw new Error('Server returned an unexpected response format');
        }
      }
      
      const responseData = await response.json();
      console.log('Delete successful, response:', responseData);
      
      return { success: true };
    } catch (error) {
      console.error(`Error deleting file '${filePath}':`, error);
      return { success: false, error: error.message };
    }
  };

  const confirmDelete = async () => {
    try {
      setIsProcessing(true);
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Determine if the current user is the creator
      const isCreator = selectedEvent.createdBy?.userId === userData.userId;
      
      if (isCreator) {
        // CREATOR DELETION FLOW
        // 1. Get the current event date document to collect all file paths
        const eventDateRef = doc(db, collectionName, selectedEvent.id, 'eventDates', selectedDate);
        
        // Delete all subcollections first (attachments, tasks, notes, etc.)
        try {
          // Delete attachments subcollection
          const attachmentsQuery = query(collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'attachments'));
          const attachmentsSnapshot = await getDocs(attachmentsQuery);
          
          // Get all file paths and delete files
          const filesToDelete = [];
          attachmentsSnapshot.forEach(doc => {
            const attachment = doc.data();
            if (attachment.filePath) {
              filesToDelete.push(attachment.filePath);
            }
            if (attachment.thumbnailPath && attachment.thumbnailPath !== attachment.filePath) {
              filesToDelete.push(attachment.thumbnailPath);
            }
          });
          
          // Delete files from storage
          console.log(`Found ${filesToDelete.length} files to delete`);
          const deletePromises = [];
          
          for (const filePath of filesToDelete) {
            try {
              const deletePromise = deleteFile(filePath, authToken).then(result => {
                return result;
              });
              deletePromises.push(deletePromise);
            } catch (error) {
              console.error(`Error setting up file deletion for ${filePath}:`, error);
            }
          }
          
          // Wait for all file deletions to complete (or fail)
          if (deletePromises.length > 0) {
            await Promise.allSettled(deletePromises);
          }
          
          // Delete attachment documents
          const attachmentsBatch = writeBatch(db);
          attachmentsSnapshot.forEach(doc => {
            attachmentsBatch.delete(doc.ref);
          });
          await attachmentsBatch.commit();
          
          // Delete tasks subcollection
          const tasksQuery = query(collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'tasks'));
          const tasksSnapshot = await getDocs(tasksQuery);
          
          const tasksBatch = writeBatch(db);
          tasksSnapshot.forEach(doc => {
            tasksBatch.delete(doc.ref);
          });
          await tasksBatch.commit();
          
          // Delete notes subcollection
          const notesQuery = query(collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'notes'));
          const notesSnapshot = await getDocs(notesQuery);
          
          const notesBatch = writeBatch(db);
          notesSnapshot.forEach(doc => {
            notesBatch.delete(doc.ref);
          });
          await notesBatch.commit();
          
          // Delete contacts subcollection
          const contactsQuery = query(collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'contacts'));
          const contactsSnapshot = await getDocs(contactsQuery);
          
          const contactsBatch = writeBatch(db);
          contactsSnapshot.forEach(doc => {
            contactsBatch.delete(doc.ref);
          });
          await contactsBatch.commit();
          
          // Delete schedule subcollection
          const scheduleQuery = query(collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'schedule'));
          const scheduleSnapshot = await getDocs(scheduleQuery);
          
          const scheduleBatch = writeBatch(db);
          scheduleSnapshot.forEach(doc => {
            scheduleBatch.delete(doc.ref);
          });
          await scheduleBatch.commit();
        } catch (error) {
          console.error('Error deleting subcollections:', error);
        }
        
        // 2. Update assigned users to remove this date
        try {
          const assignedUsersQuery = query(collection(db, collectionName, selectedEvent.id, 'assignedUsers'));
          const assignedUsersSnapshot = await getDocs(assignedUsersQuery);
          
          for (const userDoc of assignedUsersSnapshot.docs) {
            const userData = userDoc.data();
            const assignedDates = userData.assignedDates || [];
            
            // Check if this user has this date assigned
            const dateIndex = assignedDates.findIndex(date => date.dateDocId === selectedDate);
            if (dateIndex !== -1) {
              // Remove this date from the user's assigned dates
              assignedDates.splice(dateIndex, 1);
              
              // Update the user's assignedDates
              await updateDoc(userDoc.ref, {
                assignedDates
              });
              
              // Update user stats if this was a verified date
              if (assignedDates[dateIndex]?.verified) {
                await decrementUserStats(userData.userId, {
                  decrementVerifiedCount: true
                });
              }
            }
          }
        } catch (error) {
          console.error('Error updating assigned users:', error);
        }
        
        // 3. Delete the event date document
        await deleteDoc(eventDateRef);
        
        // 4. Update the events array in the context
        if (selectedEvent.eventDates) {
          const updatedEventDates = selectedEvent.eventDates.filter(date => date.id !== selectedDate);
          const updatedEvent = {
            ...selectedEvent,
            eventDates: updatedEventDates
          };
          
          setSelectedEvent(updatedEvent);
          setSelectedDate(null);
        }
        
        // 5. Navigate back to profile
        navigate(`/${userData.userName}`);
      } else {
        // NON-CREATOR DELETION FLOW - Remove just this user from the date
        try {
          // Update the assigned user document
          const assignedUserRef = doc(db, collectionName, selectedEvent.id, 'assignedUsers', userData.userId);
          const assignedUserDoc = await getDoc(assignedUserRef);
          
          if (assignedUserDoc.exists()) {
            const assignedUserData = assignedUserDoc.data();
            const assignedDates = assignedUserData.assignedDates || [];
            
            // Check if this user has this date assigned
            const dateIndex = assignedDates.findIndex(date => date.dateDocId === selectedDate);
            if (dateIndex !== -1) {
              // Get verification status before removal
              const wasVerified = assignedDates[dateIndex].verified === true;
              
              // Remove this date from the user's assigned dates
              assignedDates.splice(dateIndex, 1);
              
              // Update the user's assignedDates
              await updateDoc(assignedUserRef, {
                assignedDates
              });
              
              // Delete the user_event_date document
              try {
                const userEventDateDocId = `${userData.userId}_${selectedEvent.id}_${selectedDate}`;
                await deleteDoc(doc(db, 'user_event_dates', userEventDateDocId));
              } catch (error) {
                console.error('Error deleting user_event_date document:', error);
              }
              
              // Update user stats if this was a verified date
              if (wasVerified) {
                await decrementUserStats(userData.userId, {
                  decrementVerifiedCount: true
                });
              } else {
                await decrementUserStats(userData.userId, {
                  decrementShowCount: true
                });
              }
              
              // If all dates are removed, remove from user_events
              if (assignedDates.length === 0) {
                try {
                  await deleteDoc(doc(db, 'user_events', userData.userId, 'events', selectedEvent.id));
                } catch (error) {
                  console.error('Error deleting user_events document:', error);
                }
              }
            }
          }
          
          // Navigate back to profile or to another date
          navigate(`/${userData.userName}`);
        } catch (error) {
          console.error('Error removing user from event date:', error);
          alert('Failed to remove from event date. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error deleting event date:', error);
      alert('Failed to delete event date. Please try again.');
    } finally {
      setIsProcessing(false);
      setShowDeleteModal(false);
    }
  };

  // Handler functions
  const handleEventSelect = (event) => {
    setSelectedEvent(event);
  };

  const handleAddSchedule = async (scheduleData, isEditing) => {
    if (!selectedEvent?.id || !selectedDate) return;
  
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
  
      // Sanitize the schedule data to remove any undefined values
      const sanitizedScheduleData = Object.entries(scheduleData).reduce((acc, [key, value]) => {
        // Skip undefined values entirely
        if (value !== undefined && key !== 'docId') { // Skip docId field too
          acc[key] = value;
        }
        return acc;
      }, {});
  
      if (isEditing) {
        // Update existing schedule using docId
        const scheduleRef = doc(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'schedule', scheduleData.docId || scheduleData.id);
        await updateDoc(scheduleRef, {
          ...sanitizedScheduleData,
          updatedAt: serverTimestamp()
        });
      } else {
        // Add new schedule
        const scheduleRef = collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'schedule');
        await addDoc(scheduleRef, {
          ...sanitizedScheduleData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
  
      // Refresh schedules list, maintaining docId pattern
      const schedulesQuery = query(
        collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'schedule'),
        orderBy('createdAt', 'desc')
      );
      
      const schedulesSnapshot = await getDocs(schedulesQuery);
      const schedulesList = schedulesSnapshot.docs.map(doc => ({
        docId: doc.id,
        ...doc.data()
      }));
      
      setSchedules(schedulesList);
      setIsModalVisible(false);
      setEditingSchedule(null);
    } catch (error) {
      console.error('Error adding/updating schedule:', error);
      alert('Failed to save schedule. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  

  const handleEditSchedule = (schedule) => {
    // Make sure we're using the complete schedule object with docId
    setEditingSchedule(schedule);
    setIsModalVisible(true);
  };
  

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;
    if (!selectedEvent?.id || !selectedDate) return;
  
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // scheduleId is now the document ID (docId), so use it directly
      const scheduleRef = doc(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'schedule', scheduleId);
      
      await deleteDoc(scheduleRef);
      
      // Update schedules state - filter by docId instead of id
      setSchedules(prevSchedules => prevSchedules.filter(schedule => schedule.docId !== scheduleId));
    } catch (error) {
      console.error('Error deleting schedule:', error);
      alert('Failed to delete schedule. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigateToAttachments = () => {
    if (selectedEvent?.id && selectedDate) {
      navigate(`/${eventType}/${selectedEvent.id}/${selectedDate}/attachments`);
    }
  };

  const handleNavigateToTasks = () => {
    if (selectedEvent?.id && selectedDate) {
      navigate(`/${eventType}/${selectedEvent.id}/${selectedDate}/tasks`);
    }
  };

  const getFormattedDate = () => {
    const eventDate = getSelectedDate();
    if (!eventDate?.date) return '';
    
    try {
      let date = eventDate.date;
      if (date.seconds) {
        date = new Date(date.seconds * 1000);
      } else if (typeof date.toDate === 'function') {
        date = date.toDate();
      } else {
        date = new Date(date);
      }

      if (!date || isNaN(date.getTime())) {
        console.error('Invalid date value:', date);
        return '';
      }

      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const getVenueName = () => {
    const eventDate = getSelectedDate();
    return eventDate?.event?.venueDetails?.venueData?.displayName || '';
  };

  // Filter schedules based on visibility
  const filteredSchedules = schedules.filter(schedule => {
    if (visibilityFilter === 'All') return true;
    return schedule.visibility === visibilityFilter;
  });

  // Contact Card Component
  const ContactCard = ({ contact }) => (
    <div className={styles['contact-card']}>
      <div className={styles['contact-info']}>
        <div className={styles['contact-header']}>
          <FontAwesomeIcon 
            icon={contact.role === 'driver' ? faCar : faUserTie} 
            className={styles['contact-icon']}
          />
          <div className={styles['contact-details']}>
            <h4 className={styles['contact-name']}>{contact.name}</h4>
            <p className={styles['contact-role']}>{contact.role.charAt(0).toUpperCase() + contact.role.slice(1)}</p>
          </div>
        </div>
        
        <div className={styles['contact-phone']}>
          <FontAwesomeIcon icon={faPhone} />
          <a href={`tel:${contact.phone}`} className={styles['phone-link']}>
            {contact.phone}
          </a>
        </div>

        {contact.role === 'driver' && contact.vehicleDetails && (
          <div className={styles['vehicle-info']}>
            <FontAwesomeIcon icon={faCar} />
            <span>
              {contact.vehicleDetails.name} • {contact.vehicleDetails.color} • {contact.vehicleDetails.number}
            </span>
          </div>
        )}
      </div>
      
      <div className={styles['contact-actions']}>
        <button
          className={styles['edit-contact-btn']}
          onClick={() => handleEditContact(contact)}
        >
          <FontAwesomeIcon icon={faEdit} />
        </button>
        <button
          className={styles['delete-contact-btn']}
          onClick={() => handleDeleteContact(contact.id)}
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
      </div>
    </div>
  );

  // Note Component
  const NoteItem = ({ note }) => {
    const isAuthor = note.createdBy?.userId === userData.userId;
    const noteDate = note.createdAt ? new Date(
      note.createdAt.seconds ? note.createdAt.seconds * 1000 : note.createdAt
    ) : new Date();
    
    // Add this function to handle navigation to user profile
    const navigateToUserProfile = (userName) => {
      if (userName) {
        navigate(`/${userName}`);
      }
    };
    
    return (
      <div className={`${styles.noteItem} ${isAuthor ? styles.ownNote : ''}`}>
        <div className={styles.noteHeader}>
          <div className={styles.noteAuthor}>
            <img 
              src={note.createdBy?.profilePictureUrl || defaultUserProfilePicture} 
              alt={note.createdBy?.fullName || 'User'} 
              className={styles.noteAuthorAvatar}
              onError={(e) => {e.target.src = defaultUserProfilePicture}}
            />
            {/* Make the username clickable */}
            <span 
              className={styles.authorName}
              onClick={() => navigateToUserProfile(note.createdBy?.userName)}
            >
              {note.createdBy?.fullName || 'Unknown User'}
            </span>
          </div>
          <div className={styles.noteDate}>
            {noteDate.toLocaleDateString()} {noteDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
          </div>
        </div>
        
        {editingNoteId === note.id ? (
          <div className={styles.editNoteForm}>
            <textarea
              value={editingNoteContent}
              onChange={(e) => setEditingNoteContent(e.target.value)}
              className={styles.editNoteTextarea}
            />
            <div className={styles.editNoteActions}>
              <button 
                onClick={() => setEditingNoteId(null)}
                className={styles.cancelEditButton}
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveEditedNote}
                className={styles.saveEditButton}
                disabled={isSavingNote}
              >
                {isSavingNote ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.noteContent}>
              {note.content}
            </div>
            {isAuthor && (
              <div className={styles.noteActions}>
                <button 
                  onClick={() => handleEditNote(note)}
                  className={styles.editNoteButton}
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
                <button 
                  onClick={() => handleDeleteNote(note.id)}
                  className={styles.deleteNoteButton}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Render functions
  const renderScheduleTab = () => (
    <>
      <button 
        className={styles['add-schedule-btn']}
        onClick={() => setIsModalVisible(true)}
      >
        <FontAwesomeIcon icon={faPlus} />
        Add Schedule
      </button>           
      
      <button className={styles['section-button']} onClick={handleNavigateToAttachments}>
        <span className={styles['section-button-text']}>
          <FontAwesomeIcon icon={faPaperclip} />
          Attachments
          {attachmentCount > 0 && (
            <span className={styles['counter-badge']}>{attachmentCount}</span>
          )}
        </span>
        <FontAwesomeIcon icon={faChevronRight} />
      </button>

      <button className={styles['section-button']} onClick={handleNavigateToTasks}>
        <span className={styles['section-button-text']}>
          <FontAwesomeIcon icon={faTasks} />
          Tasks
          {taskCount > 0 && (
            <span className={styles['counter-badge']}>{taskCount}</span>
          )}
        </span>
        <FontAwesomeIcon icon={faChevronRight} />
      </button>

      {/* Contact Info Section */}
      <div className={styles['contact-section']}>
        <div className={styles['contact-header']}>
          <h3 className={styles['section-title']}>
            <FontAwesomeIcon icon={faAddressBook} />
            Contact Info
          </h3>
          <button 
            className={styles['add-contact-btn']}
            onClick={() => setIsContactModalVisible(true)}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
        
        <div className={styles['contact-list']}>
          {contacts.length === 0 ? (
            <p className={styles['no-contacts']}>No contacts added yet</p>
          ) : (
            contacts.map((contact) => (
              <ContactCard key={contact.id} contact={contact} />
            ))
          )}
        </div>
      </div>

      {/* Notes Section - now showing all notes from different users */}
      <div className={`${styles['notes-section']} ${isNotesExpanded ? styles.expanded : ''}`}>
        <div className={styles['notes-header']} onClick={() => setIsNotesExpanded(!isNotesExpanded)}>
          <h3 className={styles['section-title']}>
            <FontAwesomeIcon icon={faComments} />
            Notes
          </h3>
          <FontAwesomeIcon 
            icon={isNotesExpanded ? faChevronUp : faChevronDown} 
            className={styles['expand-icon']}
          />
        </div>
        
        <div className={styles['notes-section']}>
  {/* Add note form - now outside the expandable area */}
  <form onSubmit={handleAddNote} className={styles['add-note-form']}>
    <textarea
      value={newNote}
      onChange={(e) => setNewNote(e.target.value)}
      placeholder="Add a note..."
      className={styles['note-textarea']}
      required
    />
    <button 
      type="submit" 
      className={styles['add-note-btn']}
      disabled={isSavingNote}
    >
      {isSavingNote ? 'Adding...' : 'Add Note'}
    </button>
  </form>
  
  {/* Notes header with count and toggle */}
  <div className={styles['notes-header']} onClick={() => setIsNotesExpanded(!isNotesExpanded)}>
    <h3 className={styles['section-title']}>
      <FontAwesomeIcon icon={faComments} />
      Notes
      {notes.length > 0 && (
        <span className={styles['notes-count']}>{notes.length}</span>
      )}
    </h3>
    <FontAwesomeIcon 
      icon={isNotesExpanded ? faChevronUp : faChevronDown} 
      className={styles['expand-icon']}
    />
  </div>
  
  {/* Expandable notes list */}
  {isNotesExpanded && (
    <div className={styles['notes-content']}>
      <div className={styles['notes-list']}>
        {notes.length === 0 ? (
          <p className={styles['no-notes']}>No notes added yet</p>
        ) : (
          notes.map((note) => (
            <NoteItem key={note.id} note={note} />
          ))
        )}
      </div>
    </div>
  )}

          </div>
      
      </div>

      <div className={styles['schedules-section']}>
        <div className={styles['section-header']}>
          <h3 className={styles['section-title']}>Schedule</h3>
          <select
            className={styles['filter-dropdown']}
            value={visibilityFilter}
            onChange={(e) => setVisibilityFilter(e.target.value)}
          >
            <option value="All">All</option>
            <option value="Artist">Artists Only</option>
            <option value="Crew">Crew Only</option>
            <option value="Me">Only Me</option>
            <option value="Custom">Custom</option>
          </select>
        </div>

        <div className={styles['schedules-list']}>
            {filteredSchedules.length === 0 ? (
              <div className={styles['empty-state']}>
                <p>No schedules created yet</p>
              </div>
            ) : (
              filteredSchedules.map((schedule) => (
                <ScheduleCard
                  key={schedule.id}
                  schedule={schedule}
                  eventId={selectedEvent.id}
                  dateId={selectedDate}
                  eventType={eventType}
                  onUpdate={updateDate}
                  onEdit={handleEditSchedule}
                  onDelete={handleDeleteSchedule}
                  contacts={contacts}
                />
              ))
            )}
          </div>
      </div>
    </>
  );

  const renderTabs = () => {
    // Check if user is Vendor, Staff, or Organization
    const isVendorStaffOrg = userData?.userType === 'Vendor' || 
                            userData?.userType === 'Staff' || 
                            userData?.userType === 'Organization';
    
    return (
      <div className={styles['schedule-tabs']}>
        {!isVendorStaffOrg && (
          <button
            className={`${styles['tab-button']} ${activeTab === 'schedule' ? styles.active : ''}`}
            onClick={() => setActiveTab('schedule')}
          >
            <FontAwesomeIcon icon={faCalendarCheck} />
            <span>Schedule</span>
          </button>
        )}
        
        <button
          className={`${styles['tab-button']} ${activeTab === 'event' ? styles.active : ''}`}
          onClick={() => setActiveTab('event')}
        >
          <FontAwesomeIcon icon={faBuilding} />
          <span>Event</span>
        </button>
        
        {isVendorStaffOrg && (
          <button
            className={`${styles['tab-button']} ${activeTab === 'assignedInventory' ? styles.active : ''}`}
            onClick={() => setActiveTab('assignedInventory')}
          >
            <FontAwesomeIcon icon={faSuitcase} />
            <span>Assigned Inventory</span>
          </button>
        )}
        
        {!isVendorStaffOrg && (
          <button
            className={`${styles['tab-button']} ${activeTab === 'hotel' ? styles.active : ''}`}
            onClick={() => setActiveTab('hotel')}
          >
            <FontAwesomeIcon icon={faHotel} />
            <span>Hotel</span>
          </button>
        )}
        
        {!isVendorStaffOrg && (
          <button
            className={`${styles['tab-button']} ${activeTab === 'route' ? styles.active : ''}`}
            onClick={() => setActiveTab('route')}
          >
            <FontAwesomeIcon icon={faRoute} />
            <span>Route</span>
          </button>
        )}
      </div>
    );
  };

  const renderHeader = () => {
    const eventDate = getSelectedDate();
    let displayDate = getFormattedDate();
    let originalDate = null;
  
    if (isRescheduled && rescheduledDate) {
      displayDate = new Date(rescheduledDate).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
      originalDate = new Date(eventDate.date).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    }
  
    return (
      <div className={styles['schedule-header']}>
        <div className={styles['header-date']}>
          <h2>{displayDate}</h2>
          {isRescheduled && originalDate && (
            <div className={styles['rescheduled-info']}>
              Rescheduled from: {originalDate}
            </div>
          )}
          {isCancelled && <div className={styles['cancelled-info']}>Cancelled</div>}
        </div>
        <div className={styles['header-actions']}>
          <button 
            className={styles.actionButton}
            onClick={handleRescheduleEventDate}
            title="Reschedule Event Date"
          >
            <FontAwesomeIcon icon={faCalendar} />
          </button>
          <button 
            className={styles.actionButton}
            onClick={handleCancelEventDate}
            title={isCancelled ? "Un-cancel Event Date" : "Cancel Event Date"}
          >
            <FontAwesomeIcon icon={isCancelled ? faCheckCircle : faBan} />
          </button>
          <button 
            className={styles.actionButton}
            onClick={handleDeleteEventDate}
            title="Delete Event Date"
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>

         {/* Add verification button */}
         <div className={styles['verify-attendance']}>
          {isShowVerified ? (
            <div className={styles['verification-tag']}>
              <FontAwesomeIcon icon={faCheckCircle} />
              <span>Show Verified</span>
            </div>
          ) : (
            <button 
              className={styles['verify-button']}
              onClick={handleVerifyAttendance}
              disabled={isVerifying || isCancelled}
            >
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              {isVerifying ? 'Verifying...' : 'Verify Show'}
            </button>
          )}
          {verificationStatus && (
            <div className={`${styles['verification-status']} ${
              verificationStatus.includes('successfully') 
                ? styles['success'] 
                : styles['error']
            }`}>
              {verificationStatus}
            </div>
          )}
        </div>
  
        <div className={styles['header-address']}>
          <p>{getVenueName()}</p>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'schedule':
        return renderScheduleTab();
      case 'event':
        return <Event />;
      case 'assignedInventory':
        return <AssignedInventoryTab dateId={dateId} vendorId={vendorIdForInventory} />;
      case 'hotel':
        return <Hotel />;
      case 'route':
        return <Route />;
      default:
        return null;
    }
  };

  if (!selectedEvent || !selectedDate) {
    return (
      <div className={styles['schedule-layout']}>
        <div className={styles['main-layout']}>
          {!isMobile && <LeftMenu selectedEvent={selectedEvent} />}
          {!isMobile && (
            <RightMenu
              selectedEvent={selectedEvent}
              events={events}
              onEventSelect={handleEventSelect}
            />
          )}
          <main className={styles['schedule-main']}>
            <div className={styles['empty-state']}>
              <FontAwesomeIcon icon={faInfoCircle} size="2x" />
              <p>Please select an event and date to view schedule</p>
            </div>
          </main>
        </div>
      </div>
    );
  }
  
  const eventName = eventType === 'show' ? 
    (selectedEvent?.showName || selectedEvent?.tourName) : 
    selectedEvent?.tourName;
  
  return (
    <div className={styles['schedule-layout']}>
      <div className={styles['main-layout']}>
        {!isMobile && <LeftMenu selectedEvent={selectedEvent} />}
        {!isMobile && (
          <RightMenu
            selectedEvent={selectedEvent}
            events={events}
            onEventSelect={handleEventSelect}
          />
        )}
        <main className={styles['schedule-main']}>
          <div className={styles.header}>
            <h1>{eventName}</h1>
          </div>
          
          {renderHeader()}
          {renderTabs()}
          <div className={styles['content-section']}>
            {renderTabContent()}
          </div>
  
          {/* Modals */}
          {isModalVisible && (
            <AddScheduleModal
              isOpen={isModalVisible}
              onClose={() => {
                setIsModalVisible(false);
                setEditingSchedule(null);
              }}
              onSave={handleAddSchedule}
              eventId={selectedEvent.id}
              dateId={selectedDate}
              eventType={eventType}
              membershipTier={userData?.membershipTier}
              editingSchedule={editingSchedule}
              contacts={contacts}
            />
          )}

          {isContactModalVisible && (
            <ContactModal
              isOpen={isContactModalVisible}
              onClose={() => {
                setIsContactModalVisible(false);
                setEditingContact(null);
              }}
              onSave={handleAddContact}
              editingContact={editingContact}
            />
          )}
  
          {showRescheduleModal && <RescheduleModal />}
  
          <DeleteConfirmationModal 
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDelete}
            eventName={deleteModalProps.eventName}
            isProcessing={isProcessing}
            isCreator={deleteModalProps.isCreator}
            isVerified={deleteModalProps.isVerified}
          />

          {showCancelModal && (
            <CancelConfirmationModal 
              isOpen={showCancelModal}
              onClose={() => setShowCancelModal(false)}
              onConfirm={confirmCancel}
              eventName={eventName}
              isProcessing={isProcessing}
              isCancelled={isCancelled}
            />
          )}
      
          {isProcessing && (
            <div className={styles['loading-overlay']}>
              <div className={styles['loading-spinner']}></div>
              <p className={styles['loading-text']}>Processing...</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Schedule;